import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Info, Warehouse, ListTree, Layers, Loader2, ChevronDown, ChevronRight } from "lucide-react";

export function GroupNode({ group, selected, onSelect, depth = 0 }) {
  const [open, setOpen] = useState(depth === 0);
  const hasKids = group.children && group.children.length > 0;
  const active = selected === group.path;
  if (depth > 4) return null;
  return React.createElement("div", null,
    React.createElement("div", { className: "flex items-center gap-0.5", style: { paddingLeft: depth * 8 } },
      hasKids
        ? React.createElement("button", { onClick: () => setOpen(!open), className: "w-3 h-3 flex items-center justify-center text-slate-500" },
            open ? React.createElement(ChevronDown, { className: "w-2.5 h-2.5" }) : React.createElement(ChevronRight, { className: "w-2.5 h-2.5" })
          )
        : React.createElement("span", { className: "w-3" }),
      React.createElement("button", { onClick: () => onSelect(group.path), className: `text-[9px] truncate text-left ${active ? "text-indigo-400 font-bold" : "text-slate-400 hover:text-slate-200"}` }, `+ ${group.name}`)
    ),
    open && hasKids && group.children.map(c =>
      React.createElement(GroupNode, { key: c.path, group: c, selected, onSelect, depth: depth + 1 })
    )
  );
}

export function ProductDetail({ product, tab, setTab }) {
  const tabs = [
    { key: "info", label: "Thông tin", icon: Info },
    { key: "stock", label: "Thẻ kho", icon: Warehouse },
    { key: "ingredients", label: "Thành phần", icon: ListTree },
    { key: "addons", label: "Món thêm", icon: Layers },
  ];

  return (
    <div className="border-t border-indigo-500/20 px-4 py-3">
      <div className="flex gap-4 border-b border-[#334155] mb-3">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1 px-2 pb-2 text-[10px] border-b-2 transition-colors ${tab === t.key ? "border-indigo-400 text-indigo-400" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            <t.icon className="w-3 h-3" />{t.label}
          </button>
        ))}
      </div>
      {tab === "info" && <InfoTab product={product} />}
      {tab === "stock" && <StockTab product={product} />}
      {tab === "ingredients" && <IngredientsTab product={product} />}
      {tab === "addons" && <AddonsTab product={product} />}
    </div>
  );
}

function InfoTab({ product }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <h3 className="text-sm font-bold text-indigo-400">{product.name}</h3>
        <div className="flex gap-2 text-[10px]">
          {product.active !== false && <Badge className="bg-emerald-500/20 text-emerald-400 border-0">Bán trực tiếp</Badge>}
          <Badge className="bg-sky-500/20 text-sky-400 border-0">Tích điểm</Badge>
        </div>
      </div>
      <div className="space-y-1.5 text-[10px]">
        <InfoRow label="Mã hàng hóa" value={product.kv_code || ""} />
        <InfoRow label="Loại thực đơn" value={product.product_type || "—"} />
        <InfoRow label="Nhóm hàng" value={product.full_category || product.category_name || "—"} />
        <InfoRow label="Giá trước thuế" value={formatVND(product.price || 0)} />
        <InfoRow label="Thuế bán hàng" value={product.tax || "8%"} />
        <InfoRow label="Giá sau thuế" value={formatVND(Math.round((product.price || 0) * 1.08))} />
        <InfoRow label="Giá vốn" value={formatVND(product.cost || 0)} />
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (<div className="flex justify-between"><span className="text-slate-500">{label}:</span><span className="text-slate-200 font-medium">{value}</span></div>);
}

function StockTab({ product }) {
  const { data, isLoading } = useQuery({ queryKey: ["stock-card", product.id], queryFn: () => api.get(`/products/${product.id}/stock-card`).then(r => r.data) });
  if (isLoading) return <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />;
  const cards = data?.stock_cards || [];
  if (!cards.length) return <p className="text-[10px] text-slate-500">Chưa có giao dịch kho</p>;
  return (
    <table className="w-full text-[9px]">
      <thead><tr className="text-slate-500 uppercase"><th className="py-1 text-left">Chứng từ</th><th className="py-1 text-left">Phương thức</th><th className="py-1 text-left">Thời gian</th><th className="py-1 text-right">Giá vốn</th><th className="py-1 text-right">Số lượng</th></tr></thead>
      <tbody>{cards.slice(0, 10).map((c, i) => (<tr key={i} className="border-t border-[#334155]/30"><td className="py-1 text-indigo-400">{c.doc_code}</td><td className="py-1 text-slate-400">{c.doc_type}</td><td className="py-1 text-slate-400">{c.date?.slice(0, 16)?.replace("T", " ")}</td><td className="py-1 text-right text-slate-300">{formatVND(c.price || 0)}</td><td className={`py-1 text-right font-medium ${c.quantity < 0 ? "text-red-400" : "text-emerald-400"}`}>{c.quantity}</td></tr>))}</tbody>
    </table>
  );
}

function IngredientsTab({ product }) {
  const [form, setForm] = useState({ ingredient_code: "", ingredient_name: "", quantity: "", unit: "" });
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["ingredients", product.id], queryFn: () => api.get(`/products/${product.id}/ingredients`).then(r => r.data) });
  if (isLoading) return <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />;
  const items = data?.ingredients || [];

  const handleAdd = async () => {
    if (!form.ingredient_name) return;
    try {
      await api.post(`/products/${product.id}/ingredients/add?ingredient_code=${encodeURIComponent(form.ingredient_code)}&ingredient_name=${encodeURIComponent(form.ingredient_name)}&quantity=${form.quantity || 0}&unit=${encodeURIComponent(form.unit)}`);
      setForm({ ingredient_code: "", ingredient_name: "", quantity: "", unit: "" });
      qc.invalidateQueries({ queryKey: ["ingredients", product.id] });
    } catch {}
  };
  const handleDel = async (id) => {
    await api.delete(`/products/${product.id}/ingredients/${id}`);
    qc.invalidateQueries({ queryKey: ["ingredients", product.id] });
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5 items-end">
        <input value={form.ingredient_code} onChange={e => setForm(f => ({...f, ingredient_code: e.target.value}))} placeholder="Mã NVL" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-20" />
        <input value={form.ingredient_name} onChange={e => setForm(f => ({...f, ingredient_name: e.target.value}))} placeholder="Tên thành phần *" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-36" />
        <input value={form.quantity} onChange={e => setForm(f => ({...f, quantity: e.target.value}))} placeholder="Số lượng" type="number" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-16" />
        <input value={form.unit} onChange={e => setForm(f => ({...f, unit: e.target.value}))} placeholder="ĐVT" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-14" />
        <button onClick={handleAdd} className="bg-emerald-500 text-white rounded px-2 py-1 text-[9px] font-bold hover:bg-emerald-400">+ Thêm</button>
      </div>
      {items.length === 0 ? <p className="text-[10px] text-slate-500">Chưa có thành phần NVL.</p> : (
        <table className="w-full text-[9px]">
          <thead><tr className="text-slate-500 uppercase"><th className="py-1 w-8">STT</th><th className="py-1 text-left text-indigo-400">Mã NVL</th><th className="py-1 text-left">Tên thành phần</th><th className="py-1 text-right">Số lượng</th><th className="py-1 w-8"></th></tr></thead>
          <tbody>{items.map((ing, i) => (<tr key={ing.id} className="border-t border-[#334155]/30"><td className="py-1 text-center text-slate-500">{i+1}</td><td className="py-1 text-indigo-400">{ing.ingredient_code}</td><td className="py-1 text-slate-300">{ing.ingredient_name} {ing.unit ? `(${ing.unit})` : ""}</td><td className="py-1 text-right text-slate-200">{ing.quantity}</td><td className="py-1 text-center"><button onClick={() => handleDel(ing.id)} className="text-red-400 hover:text-red-300 text-[9px]">x</button></td></tr>))}</tbody>
        </table>
      )}
    </div>
  );
}

function AddonsTab({ product }) {
  const [form, setForm] = useState({ addon_code: "", addon_name: "", price: "" });
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["addons", product.id], queryFn: () => api.get(`/products/${product.id}/addons`).then(r => r.data) });
  if (isLoading) return <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />;
  const items = data?.addons || [];

  const handleAdd = async () => {
    if (!form.addon_name) return;
    try {
      await api.post(`/products/${product.id}/addons/add?addon_code=${encodeURIComponent(form.addon_code)}&addon_name=${encodeURIComponent(form.addon_name)}&price=${form.price || 0}`);
      setForm({ addon_code: "", addon_name: "", price: "" });
      qc.invalidateQueries({ queryKey: ["addons", product.id] });
    } catch {}
  };
  const handleDel = async (id) => {
    await api.delete(`/products/${product.id}/addons/${id}`);
    qc.invalidateQueries({ queryKey: ["addons", product.id] });
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5 items-end">
        <input value={form.addon_code} onChange={e => setForm(f => ({...f, addon_code: e.target.value}))} placeholder="Mã topping" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-20" />
        <input value={form.addon_name} onChange={e => setForm(f => ({...f, addon_name: e.target.value}))} placeholder="Tên món thêm *" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-36" />
        <input value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} placeholder="Giá bán" type="number" className="bg-[#1e293b] border border-[#334155] rounded px-2 py-1 text-[9px] text-slate-200 w-24" />
        <button onClick={handleAdd} className="bg-emerald-500 text-white rounded px-2 py-1 text-[9px] font-bold hover:bg-emerald-400">+ Thêm</button>
      </div>
      {items.length === 0 ? <p className="text-[10px] text-slate-500">Chưa có món thêm (topping).</p> : (
        <table className="w-full text-[9px]">
          <thead><tr className="text-slate-500 uppercase"><th className="py-1 text-left text-indigo-400">Mã</th><th className="py-1 text-left">Món thêm</th><th className="py-1 text-right">Giá bán</th><th className="py-1 w-8"></th></tr></thead>
          <tbody>{items.map((a) => (<tr key={a.id} className="border-t border-[#334155]/30"><td className="py-1 text-indigo-400">{a.addon_code}</td><td className="py-1 text-slate-300">{a.addon_name}</td><td className="py-1 text-right text-slate-200">{formatVND(a.price)}</td><td className="py-1 text-center"><button onClick={() => handleDel(a.id)} className="text-red-400 hover:text-red-300 text-[9px]">x</button></td></tr>))}</tbody>
        </table>
      )}
    </div>
  );
}
